import type { ColumnSizingState, Header } from '@tanstack/react-table';

export interface ColumnTrackOptions {
  columnSizing: ColumnSizingState;
  minColumnSize: number;
  isReorderable: boolean;
}

/* One column's CSS grid track. The loaded table and its loading state both
 * read it, so the placeholder bars sit exactly where the data will land. */
export function columnTrack<T>(
  header: Header<T, unknown>,
  { columnSizing, minColumnSize, isReorderable }: ColumnTrackOptions,
) {
  if (header.column.getIsResizing() || header.column.id in columnSizing) {
    return intToGridUnit(header.column.getSize());
  }

  const unit = header.column.columnDef.meta?.sizeUnit ?? 'px';

  if (header.column.columnDef.size !== undefined) {
    const gridUnit = intToGridUnit(header.column.columnDef.size, unit);
    if (header.column.getIsFirstColumn()) {
      return `calc(${isReorderable ? '1' : '0.25'}rem + ${gridUnit})`;
    }
    return gridUnit;
  }

  if (
    header.column.columnDef.minSize !== undefined ||
    header.column.columnDef.maxSize !== undefined
  ) {
    return `minmax(${intToGridUnit(header.column.columnDef.minSize, unit)}, ${intToGridUnit(
      header.column.columnDef.maxSize,
      unit,
    )})`;
  }

  return `minmax(${intToGridUnit(minColumnSize, unit)}, 1fr)`;
}

function intToGridUnit(value?: number, unit = 'px') {
  return value !== undefined ? `${value}${unit}` : '1fr';
}
